import React, { useEffect, useState } from 'react';
import { Smartphone, X, Heart } from 'lucide-react';

/**
 * Lightweight PWA install prompt.
 * Listens for `beforeinstallprompt` (Chrome / Edge / Android) and renders a
 * subtle bottom card the user can dismiss or accept. iOS Safari doesn't fire
 * this event — we fall back to a small "Add to Home Screen" hint there.
 *
 * The user's dismissal is remembered in localStorage so we don't pester them.
 */
const DISMISSED_KEY = 'wf_pwa_dismissed_at';
const SUPPRESS_FOR_MS = 1000 * 60 * 60 * 24 * 14; // 2 weeks

function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator?.standalone === true
  );
}

function isIos() {
  const ua = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua) && !/crios|fxios|edgios/.test(ua);
}

export default function InstallPrompt() {
  const [event, setEvent] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissed = Number(localStorage.getItem(DISMISSED_KEY) || 0);
    if (dismissed && Date.now() - dismissed < SUPPRESS_FOR_MS) return;

    const handler = (e) => {
      e.preventDefault();
      setEvent(e);
      // Reveal after a short delay so it doesn't ambush first paint.
      setTimeout(() => setShow(true), 6000);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS fallback — show a hint once after a delay
    if (isIos()) {
      const t = setTimeout(() => setShow(true), 9000);
      return () => clearTimeout(t);
    }
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  };

  const install = async () => {
    if (!event) return dismiss();
    event.prompt();
    try {
      await event.userChoice;
    } catch (e) { /* ignore */ }
    dismiss();
  };

  if (!show) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed inset-x-4 bottom-4 sm:inset-x-auto sm:right-6 sm:left-auto sm:bottom-6 sm:w-[360px] z-40 animate-float-up"
    >
      <div className="rounded-2xl border border-[#EFE3D8] bg-white shadow-xl p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[#F4E4DA] flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-5 h-5 text-[#7B2C3B]" />
        </div>
        <div className="flex-1">
          <p className="font-serif text-base text-[#2A2A2A] inline-flex items-center gap-1">
            Install WeddingFriends <Heart className="w-3 h-3 fill-[#7B2C3B] text-[#7B2C3B]" />
          </p>
          {isIos() && !event ? (
            <p className="text-xs text-[#6A6A6A] mt-1">
              Tap <span className="font-medium">Share</span> in Safari, then <span className="font-medium">&quot;Add to Home Screen&quot;</span> for fast offline-friendly access.
            </p>
          ) : (
            <p className="text-xs text-[#6A6A6A] mt-1">Add the app to your home screen for one-tap access — no browser bar, just your wedding.</p>
          )}
          <div className="flex items-center gap-2 mt-3">
            {event && (
              <button
                data-testid="pwa-install-accept"
                onClick={install}
                className="px-3 py-1.5 rounded-full bg-[#7B2C3B] text-[#FBF7F2] text-xs font-medium"
              >
                Install
              </button>
            )}
            <button
              data-testid="pwa-install-dismiss"
              onClick={dismiss}
              className="text-xs text-[#6A6A6A] hover:text-[#2A2A2A]"
            >
              Not now
            </button>
          </div>
        </div>
        <button onClick={dismiss} aria-label="Close" className="text-[#9A8A7E] hover:text-[#2A2A2A] -mt-1 -mr-1 p-1">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
